import { type CheckboxProps } from '@/app/components/forms/checkbox';
import ThailandDistrict from '@/app/components/sections/dealer/utils/json/thailand-district.json';
import ThailandProvince from '@/app/components/sections/dealer/utils/json/thailand-province.json';
import type { GenerateBaseMetadataProps } from '@/app/utils/metadata';

export enum SearchType {
  NATURAL = 'natural',
  SUGGEST = 'suggest',
  CURRENT_LOCATION = 'current-location',
}

export const DEALER_META_DATA: GenerateBaseMetadataProps = {
  title: 'ค้นหาโชว์รูมและศูนย์บริการอีซูซุ',
  description:
    'ค้นหาโชว์รูม ศูนย์บริการ ศูนย์บริการตัวถังและสีใกล้บ้านคุณได้ที่นี่ ค้นหาจากประเภท ชื่อผู้จำหน่าย จังหวัด เขต หรือเลขไปรษณีย์',
};

export const DEALER_EMPTY_STATE_DATA = {
  TITLE: 'ไม่พบข้อมูล',
  TITLE_BY_SEARCH: 'กรุณาลองใหม่อีกครั้ง',
  TITLE_BY_LOCATION: 'ไม่พบโชว์รูม / ศูนย์บริการใกล้คุณ',
  DESCRIPTION: `ตรวจดูให้แน่ใจว่าข้อความของคุณสะกดถูกต้อง <br />กรุณาค้นหาจาก <strong>จังหวัด, เขต, เลขไปรษณีย์, <span>ชื่อโชว์รูม หรือ ชื่อศูนย์บริการ</span></strong>`,
  DESCRIPTION_BY_LOCATION: `กรุณาลองค้นหาอีกครั้ง ด้วยชื่อ <strong>จังหวัด, เขต, <span>เลขไปรษณีย์, ชื่อโชว์รูม หรือ ชื่อศูนย์บริการ</span></strong>`,
};

export const MAP_DEFAULT = {
  CENTER: { lat: 13.7625534, lng: 100.572413 },
  ZOOM: 5,
  ZOOM_IN: 13,
  ZOOM_SELECTED_DEALER: 16,
};

export const DEALER_DEFAULT_VALUE = {
  NAME: 'โชว์รูมและศูนย์บริการ',
  ADDRESS: 'ไม่มีข้อมูล',
  LOCATION_DENIED: {
    title: 'การเข้าถึงตำแหน่งถูกปฏิเสธ',
    description: 'กรุณาอนุญาตการเข้าถึงตำแหน่ง',
  },
};

export const THAILAND_LOCATIONS = [...ThailandProvince, ...ThailandDistrict];

export const EARTH_RADIUS = 6371;
export const NEARBY_RADIUS = 20;
export const NEARBY_RADIUS_RANGE = [8, 15, 20];

export enum DEALER_SEARCH_KEY {
  Source = 'source',
  ServiceType = 'service-type',
  Showroom = 'showroom',
  VehicleType = 'vehicle-type',
  Service = 'service',
  General = 'general',
  BodyPaint = 'body-paint',
  ChangeTire = 'change-tire',
  Overhaul = 'overhaul',
  BusinessDay = 'business-day',
  Weekdays = 'weekdays',
  Saturday = 'sat',
  Sunday = 'sun',
  Lat = 'lat',
  Lng = 'lng',
  Q = 'q',
  Tab = 'tab',
  Bound = 'bound',
}

export const DEALER_SERVICE_TARGET_KEYS = [
  DEALER_SEARCH_KEY.Service,
  DEALER_SEARCH_KEY.ChangeTire,
  DEALER_SEARCH_KEY.Overhaul,
];

export const DEALER_SCOPE_ADVANCED_SEARCH = [
  DEALER_SEARCH_KEY.VehicleType,
  DEALER_SEARCH_KEY.ServiceType,
  DEALER_SEARCH_KEY.Showroom,
  DEALER_SEARCH_KEY.Service,
  DEALER_SEARCH_KEY.BodyPaint,
  DEALER_SEARCH_KEY.BusinessDay,
];

export const DEALER_TYPES = [
  {
    id: DEALER_SEARCH_KEY.Showroom,
    label: 'โชว์รูม',
    icon: 'IconStore',
  },
  {
    id: DEALER_SEARCH_KEY.Service,
    label: 'ศูนย์บริการ',
    icon: 'IconBuild',
  },
  {
    id: DEALER_SEARCH_KEY.BodyPaint,
    label: 'ศูนย์บริการตัวถัง / สี',
    icon: 'IconColors',
  },
];

export interface AdvancedSearch {
  id: string;
  label: string;
  checked?: boolean;
  icon?: string;
  description?: string;
  options?: CheckboxProps['options'];
}

export const ADVANCED_SEARCH: AdvancedSearch[] = [
  {
    id: DEALER_SEARCH_KEY.Showroom,
    label: 'โชว์รูม',
    checked: false,
    icon: 'IconStore',
  },
  {
    id: DEALER_SEARCH_KEY.Service,
    label: 'ศูนย์บริการ',
    icon: 'IconBuild',
    checked: false,
    description: 'บริการพิเศษ',
    options: [
      {
        label: 'เปลี่ยนยาง',
        name: DEALER_SEARCH_KEY.ChangeTire,
        checked: false,
      },
      {
        label: 'โอเวอร์ฮอล์',
        name: DEALER_SEARCH_KEY.Overhaul,
        checked: false,
      },
    ],
  },
  {
    id: DEALER_SEARCH_KEY.BodyPaint,
    label: 'ศูนย์บริการตัวถัง / สี',
    checked: false,
    icon: 'IconColors',
  },
  {
    id: DEALER_SEARCH_KEY.BusinessDay,
    label: 'วันทำการ',
    icon: 'IconCalendarMonth',
    options: [
      {
        label: 'จันทร์ - ศุกร์',
        name: DEALER_SEARCH_KEY.Weekdays,
        checked: false,
      },
      {
        label: 'เสาร์',
        name: DEALER_SEARCH_KEY.Saturday,
        checked: false,
      },
      {
        label: 'อาทิตย์',
        name: DEALER_SEARCH_KEY.Sunday,
        checked: false,
      },
    ],
  },
  {
    id: DEALER_SEARCH_KEY.VehicleType,
    label: 'ประเภทรถ',
    icon: 'IconCar',
    options: [
      {
        label: 'รถปิกอัพ และรถอเนกประสงค์',
        name: 'lcv',
        checked: false,
      },
      {
        label: 'รถบรรทุก',
        name: 'cv',
        checked: false,
      },
    ],
  },
];

export const SERVICE_REPAIR_REMARK = {
  cv: {
    ZA: 'เฉพาะรถบรรทุกอีซูซุ รุ่น N-Series',
    ZB: 'เฉพาะรถบรรทุกอีซูซุ รุ่น N-Series',
  },
  lcv: {},
};
